/**
 * Read-only live tool-run inspector. GET timeline only; never claims work or purchases.
 */
import {
  isPipelineToolRunInspectorTerminal,
  type PipelineToolRunInspectorView
} from "../../../packages/core/src/pipeline-tool-inspector.js";
import { pipelineToolTitle } from "../../../packages/core/src/pipeline-workflow.js";
import { authenticatedFetch } from "./dashboard-auth.js";
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";

export const PIPELINE_RUN_INSPECTOR_POLL_MS = 2_000;
export const PIPELINE_RUN_TIMELINE_PATH = "/dashboard/pipeline-tools/runs";

export type PipelineRunInspectorAttempt = {
  id: string;
  toolId: string;
  status: string;
  workItemId: string | null;
};

export type PipelineRunInspectorState = {
  inspectOpen: boolean;
  inspectRunId: string;
  inspectTimeline: PipelineToolRunInspectorView | null;
  inspectAttempts: PipelineRunInspectorAttempt[];
  inspectError: string | null;
};

export function pipelineRunInspectorRunId(search?: string): string {
  const query = search ?? (typeof window === "undefined" ? "" : window.location.search);
  return new URLSearchParams(query).get("inspect")?.trim() ?? "";
}

export function emptyPipelineRunInspectorState(search?: string): PipelineRunInspectorState {
  const inspectRunId = pipelineRunInspectorRunId(search);
  return {
    inspectOpen: Boolean(inspectRunId),
    inspectRunId,
    inspectTimeline: null,
    inspectAttempts: [],
    inspectError: null
  };
}

export function pipelineRunInspectorHtml(state: Pick<PipelineRunInspectorState, "inspectOpen" | "inspectRunId" | "inspectTimeline" | "inspectAttempts" | "inspectError">): string {
  if (!state.inspectOpen) return "";
  const timeline = state.inspectTimeline;
  return `
    <div class="pipeline-sheet is-on" data-run-inspector>
      <div class="pipeline-sheet-box pipeline-run-inspector-box">
        <h2>Tool run inspector</h2>
        <p class="muted">Read-only timeline from durable events. Opening this does not claim work or buy vendors.</p>
        <label class="k">Run id</label>
        <div class="pipeline-inline">
          <input type="text" data-run-inspect-id maxlength="120" value="${escapeAttr(state.inspectRunId)}" placeholder="tool_run id" />
          <button type="button" class="btn main" data-run-inspect-load>Load</button>
        </div>
        ${state.inspectError ? `<p class="pipeline-run-inspector-error" role="alert">${escapeHtml(state.inspectError)}</p>` : ""}
        ${attemptListHtml(state.inspectAttempts, state.inspectRunId)}
        ${timeline ? timelineHtml(timeline) : `<p class="muted">Enter a tool_run id to load its timeline.</p>`}
        <div class="pipeline-row">
          <button type="button" class="btn" data-run-inspect-close>Close</button>
        </div>
      </div>
    </div>
  `;
}

export function bindPipelineRunInspector(
  mount: HTMLElement,
  state: PipelineRunInspectorState,
  paint: (patch: Partial<PipelineRunInspectorState>) => void
): void {
  mount.querySelector("[data-run-inspect]")?.addEventListener("click", (event) => {
    event.stopPropagation();
    paint({ inspectOpen: true, inspectError: null });
    void loadInspector(state.inspectRunId, paint);
  });
  mount.querySelector("[data-run-inspect-close]")?.addEventListener("click", (event) => {
    event.stopPropagation();
    paint({ inspectOpen: false, inspectError: null });
  });
  mount.querySelector("[data-run-inspect-load]")?.addEventListener("click", (event) => {
    event.stopPropagation();
    const runId = mount.querySelector<HTMLInputElement>("[data-run-inspect-id]")?.value.trim() ?? "";
    paint({ inspectRunId: runId, inspectError: null });
    void loadInspector(runId, paint);
  });
  mount.querySelectorAll("[data-run-inspect-pick]").forEach((node) => {
    node.addEventListener("click", (event) => {
      event.stopPropagation();
      const runId = (node as HTMLElement).getAttribute("data-run-inspect-pick")?.trim() ?? "";
      paint({ inspectRunId: runId, inspectError: null });
      void loadInspector(runId, paint);
    });
  });
}

export function startPipelineRunInspectorPoll(mount: HTMLElement, state: PipelineRunInspectorState, paint: (patch: Partial<PipelineRunInspectorState>) => void): void {
  let inflight = false;
  let id = 0;
  const tick = (): void => {
    void (async () => {
      if (!mount.querySelector("[data-pipeline-board]")) {
        window.clearInterval(id);
        return;
      }
      if (!state.inspectOpen || !state.inspectRunId) return;
      if (state.inspectTimeline && isPipelineToolRunInspectorTerminal(state.inspectTimeline.status)) {
        return;
      }
      if (typeof document !== "undefined" && document.hidden) return;
      if (mount.querySelector("input:focus, textarea:focus, select:focus")) return;
      if (inflight) return;
      inflight = true;
      try {
        const timeline = await fetchPipelineRunTimeline(state.inspectRunId);
        if (!mount.querySelector("[data-pipeline-board]") || !state.inspectOpen) return;
        paint({ inspectTimeline: timeline, inspectError: timeline ? null : "Run not found." });
      } catch (error) {
        paint({ inspectError: error instanceof Error ? error.message : "Timeline load failed." });
      } finally {
        inflight = false;
      }
    })();
  };
  if (state.inspectOpen) tick();
  id = window.setInterval(tick, PIPELINE_RUN_INSPECTOR_POLL_MS);
}

export async function fetchPipelineRunTimeline(runId: string): Promise<PipelineToolRunInspectorView | null> {
  const id = runId.trim();
  if (!id) return null;
  const response = await authenticatedFetch(`${PIPELINE_RUN_TIMELINE_PATH}/${encodeURIComponent(id)}/timeline`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Timeline read failed (${response.status}).`);
  return (await response.json()) as PipelineToolRunInspectorView;
}

export async function fetchPipelineRunAttempts(): Promise<PipelineRunInspectorAttempt[]> {
  const response = await authenticatedFetch("/dashboard/pipeline-tools/attempts?limit=20");
  if (!response.ok) return [];
  const body = (await response.json()) as { attempts?: Array<{ id?: unknown; toolId?: unknown; status?: unknown; workItemId?: unknown }> };
  return (body.attempts ?? []).flatMap((row) => {
    const id = typeof row.id === "string" ? row.id.trim() : "";
    const toolId = typeof row.toolId === "string" ? row.toolId.trim() : "";
    const status = typeof row.status === "string" ? row.status.trim() : "";
    if (!id || !toolId) return [];
    return [{
      id,
      toolId,
      status,
      workItemId: typeof row.workItemId === "string" && row.workItemId.trim() ? row.workItemId : null
    }];
  });
}

async function loadInspector(runId: string, paint: (patch: Partial<PipelineRunInspectorState>) => void): Promise<void> {
  const attempts = await fetchPipelineRunAttempts().catch(() => []);
  if (!runId.trim()) {
    paint({ inspectAttempts: attempts, inspectTimeline: null });
    return;
  }
  try {
    const timeline = await fetchPipelineRunTimeline(runId);
    paint({
      inspectAttempts: attempts,
      inspectTimeline: timeline,
      inspectError: timeline ? null : "Run not found."
    });
  } catch (error) {
    paint({
      inspectAttempts: attempts,
      inspectError: error instanceof Error ? error.message : "Timeline load failed."
    });
  }
}

function attemptListHtml(attempts: PipelineRunInspectorAttempt[], selectedId: string): string {
  if (!attempts.length) return "";
  return `
    <label class="k">Recent attempts</label>
    <ul class="pipeline-run-inspector-attempts">
      ${attempts.map((attempt) => `
        <li>
          <button type="button" class="tiny${attempt.id === selectedId ? " is-on" : ""}" data-run-inspect-pick="${escapeAttr(attempt.id)}">
            ${escapeHtml(pipelineToolTitle(attempt.toolId))} · ${escapeHtml(attempt.status)}
          </button>
        </li>
      `).join("")}
    </ul>
  `;
}

function timelineHtml(timeline: PipelineToolRunInspectorView): string {
  const versions = [
    timeline.toolVersion ? `tool ${timeline.toolVersion}` : "",
    timeline.policyVersion ? `policy ${timeline.policyVersion}` : "",
    timeline.inputVersion ? `input ${timeline.inputVersion}` : "",
    `attempt ${timeline.attemptNo}`
  ].filter(Boolean).join(" · ");
  const duration = timeline.durationMs == null ? "" : `${Math.round(timeline.durationMs / 1000)}s`;
  const usage = timeline.usage
    ? [timeline.usage.provider, timeline.usage.billableUnits != null ? `${timeline.usage.billableUnits} units` : ""]
      .filter(Boolean)
      .join(" · ")
    : "";
  const evidence = timeline.evidence.length
    ? `<ul class="pipeline-run-inspector-evidence">${timeline.evidence.map((item) => {
      const label = item.uri ? shortEvidence(item.uri) : item.type;
      return item.uri && isSafeEvidenceHref(item.uri)
        ? `<li><a href="${escapeAttr(item.uri)}" target="_blank" rel="noopener">${escapeHtml(item.type)}: ${escapeHtml(label)}</a></li>`
        : `<li>${escapeHtml(item.type)}${item.uri ? `: ${escapeHtml(label)}` : ""}</li>`;
    }).join("")}</ul>`
    : `<p class="muted">No evidence links.</p>`;
  const events = timeline.events.length
    ? `<ol class="pipeline-run-inspector-events">${timeline.events.map((event) => `
        <li data-event-kind="${escapeAttr(event.kind)}" data-event-phase="${escapeAttr(event.phase)}">
          <strong>${escapeHtml(labelize(event.phase))}</strong>
          <span>${escapeHtml(event.message || event.kind)} · ${escapeHtml(event.at)}</span>
        </li>
      `).join("")}</ol>`
    : `<p class="muted">No durable events yet.</p>`;
  const input = Object.keys(timeline.input).length
    ? `<pre class="pipeline-run-inspector-input">${escapeHtml(JSON.stringify(timeline.input, null, 2))}</pre>`
    : "";
  return `
    <div class="pipeline-run-inspector-meta" data-run-status="${escapeAttr(timeline.status)}" data-run-stalled="${timeline.stalled ? "true" : "false"}">
      <p><strong>${escapeHtml(pipelineToolTitle(timeline.toolId))}</strong> · ${escapeHtml(timeline.status)}${duration ? ` · ${escapeHtml(duration)}` : ""}</p>
      <p class="muted">${escapeHtml(versions)}</p>
      ${timeline.currentReason ? `<p data-run-reason>${escapeHtml(timeline.currentReason)}</p>` : ""}
      ${timeline.nextAutomaticAction ? `<p data-run-next>${escapeHtml(timeline.nextAutomaticAction)}</p>` : ""}
      ${usage ? `<p data-run-usage>${escapeHtml(usage)}</p>` : ""}
    </div>
    <label class="k">Inputs</label>
    ${input || `<p class="muted">No stored inputs.</p>`}
    <label class="k">Timeline</label>
    ${events}
    <label class="k">Evidence</label>
    ${evidence}
  `;
}

function isSafeEvidenceHref(uri: string): boolean {
  return /^(https?:\/\/|r2:\/\/|\/)/i.test(uri);
}

function shortEvidence(uri: string): string {
  if (uri.length <= 72) return uri;
  return `${uri.slice(0, 32)}…${uri.slice(-24)}`;
}
